import React, { Suspense } from 'react'
import { ServiceType } from '../types'

// Loading component for lazy-loaded components
export const ComponentLoader: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600 font-medium">{message}</p>
    </div>
  </div>
)

// Service-specific loading components
export const ServiceLoader: React.FC<{ serviceType: ServiceType }> = ({ serviceType }) => {
  const serviceNames: Record<ServiceType, string> = {
    [ServiceType.HR]: 'HR Templates',
    [ServiceType.COMPLIANCE]: 'Compliance Services',
    [ServiceType.RARE_EARTH_PERMIT]: 'Rare Earth Permits',
    [ServiceType.TRADE]: 'Trade Solutions',
    [ServiceType.INTELLIGENCE]: 'Intelligence Services',
    [ServiceType.VERPACKG]: 'VerpackG-Löschung Service'
  }
  
  return (
    <ComponentLoader message={`Loading ${serviceNames[serviceType]}...`} />
  )
}

// Lazy load service-specific components
export const LazyServicePage = React.lazy(() => 
  import('./ServicePage').then(module => ({ default: module.ServicePage }))
)

export const LazyComingSoonServicePage = React.lazy(() => 
  import('./ComingSoonServicePage').then(module => ({ default: module.ComingSoonServicePage }))
)

export const LazyResponsiveDemo = React.lazy(() => 
  import('./demo/ResponsiveDemo').then(module => ({ default: module.ResponsiveDemo }))
)

export const LazySitemapViewer = React.lazy(() => 
  import('./SitemapXML').then(module => ({ default: module.SitemapViewer }))
)

export const LazySitemapXML = React.lazy(() => 
  import('./SitemapXML').then(module => ({ default: module.SitemapXML }))
)

// HOC for wrapping lazy components with Suspense
export const withSuspense = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={fallback || <ComponentLoader />}>
      <Component {...props} />
    </Suspense>
  )
  
  WrappedComponent.displayName = `withSuspense(${Component.displayName || Component.name})`
  
  return WrappedComponent
}

// Service-specific wrapped components
export const ServicePageWithSuspense = withSuspense(LazyServicePage, <ServiceLoader serviceType={ServiceType.HR} />)
export const ComingSoonServicePageWithSuspense = withSuspense(LazyComingSoonServicePage)
export const ResponsiveDemoWithSuspense = withSuspense(LazyResponsiveDemo)
export const SitemapViewerWithSuspense = withSuspense(LazySitemapViewer)
export const SitemapXMLWithSuspense = withSuspense(LazySitemapXML)